import { useCallback, useMemo, useRef, useState } from "react";
import type { TreeNodeData } from "../../Tree/types";
import { walkTree } from "../../Tree/utils/adapter";
import type { TreeSelectProps } from "../types";
import { pruneTree } from "../utils/pruneTree";
import { useTreeSelectValue } from "./useTreeSelectValue";

export function useTreeSelectState(props: TreeSelectProps) {
  const { isSearchable = false, defaultExpandedKeys } = props;
  const defaultExpandedKeysRef = useRef(defaultExpandedKeys);
  defaultExpandedKeysRef.current = defaultExpandedKeys;
  const value = useTreeSelectValue(props);

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedKeys, setExpandedKeys] = useState<string[]>(
    defaultExpandedKeys ?? []
  );

  const prunedTreeData = useMemo(
    () =>
      searchTerm.trim()
        ? pruneTree(value.treeData, searchTerm)
        : value.treeData,
    [value.treeData, searchTerm]
  );

  const effectiveExpandedKeys = useMemo(() => {
    if (!searchTerm) return expandedKeys;

    const allKeys: string[] = [];
    walkTree(prunedTreeData, node => {
      if (node.children?.length) allKeys.push(node.key);
    });

    return allKeys;
  }, [searchTerm, prunedTreeData]);

  const handleSelect = useCallback(
    (keys: string[], info: { node: TreeNodeData }) => {
      if (value.isDisabled || info.node.disabled) return;
      value.applyValue(keys[0] ?? "");
      setIsOpen(false);
      setSearchTerm("");
    },
    [value.applyValue, value.isDisabled]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (value.isDisabled) return;
      setIsOpen(open);
      if (!open) {
        setSearchTerm("");
        setExpandedKeys(defaultExpandedKeysRef.current ?? []);
      }
    },
    [value.isDisabled]
  );

  const handleExpand = useCallback(
    (keys: string[]) => setExpandedKeys(keys),
    []
  );

  return {
    ...value,
    isSearchable,
    isOpen,
    handleOpenChange,
    searchTerm,
    setSearchTerm,
    prunedTreeData,
    effectiveExpandedKeys,
    handleSelect,
    handleExpand,
  };
}
